import React from 'react';
import { Check, PlusCircle } from 'lucide-react';
import { BusinessGoal, QuestionnaireData } from '../../types';

interface GoalsStepProps {
  data: Partial<QuestionnaireData>;
  onChange: (data: Partial<QuestionnaireData>) => void;
}

const goals = [
  { id: 'increase-sales' as BusinessGoal, label: 'Increase sales/revenue' },
  { id: 'improve-marketing' as BusinessGoal, label: 'Improve marketing efforts' },
  { id: 'enhance-customer-service' as BusinessGoal, label: 'Enhance customer service' },
  { id: 'streamline-operations' as BusinessGoal, label: 'Streamline operations' },
  { id: 'reduce-costs' as BusinessGoal, label: 'Reduce costs' },
  { id: 'build-brand' as BusinessGoal, label: 'Build brand awareness' },
  { id: 'expand-customer-base' as BusinessGoal, label: 'Expand customer base' },
  { id: 'automate-tasks' as BusinessGoal, label: 'Automate tasks/processes' },
  { id: 'manage-scheduling' as BusinessGoal, label: 'Manage appointments & scheduling' },
  { id: 'track-jobs' as BusinessGoal, label: 'Track jobs and projects' },
];

const MAX_SELECTIONS = 4;

// Named export
export function GoalsStep({ data, onChange }: GoalsStepProps) {
  const selectedGoals = data.goals || [];
  const [customGoal, setCustomGoal] = React.useState('');

  const toggleGoal = (goal: BusinessGoal) => {
    if (selectedGoals.includes(goal)) {
      // Remove the goal
      const newGoals = selectedGoals.filter(g => g !== goal);
      onChange({ goals: newGoals });
    } else if (selectedGoals.length < MAX_SELECTIONS) {
      // Add the goal only if under the limit
      const newGoals = [...selectedGoals, goal];
      onChange({ goals: newGoals });
    }
  };

  const handleCustomGoal = () => {
    if (customGoal.trim() && selectedGoals.length < MAX_SELECTIONS) {
      const newGoal = customGoal.trim() as BusinessGoal;
      onChange({ goals: [...selectedGoals, newGoal] });
      setCustomGoal('');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm text-gray-600">Select up to 4 goals</p>
        <span className="text-sm font-medium text-blue-600">
          {selectedGoals.length}/{MAX_SELECTIONS}
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
        {goals.map((goal) => {
          const isSelected = selectedGoals.includes(goal.id);
          const isDisabled = !isSelected && selectedGoals.length >= MAX_SELECTIONS;

          return (
            <button
              key={goal.id}
              onClick={() => toggleGoal(goal.id)}
              disabled={isDisabled}
              className={`p-3 sm:p-4 rounded-lg border-2 flex items-center transition-all text-left min-h-[48px] ${
                isSelected
                  ? 'border-blue-600 bg-blue-50'
                  : isDisabled
                  ? 'border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed'
                  : 'border-gray-200 hover:border-blue-200 hover:bg-gray-50'
              }`}
            >
              <div className={`w-5 h-5 rounded border flex items-center justify-center mr-3 flex-shrink-0 ${
                isSelected
                  ? 'border-blue-600 bg-blue-600'
                  : 'border-gray-300'
              }`}>
                {isSelected && (
                  <Check className="w-3 h-3 text-white" />
                )}
              </div>
              <span className={`text-sm sm:text-base text-gray-700 ${isDisabled ? 'opacity-50' : ''}`}>
                {goal.label}
              </span>
            </button>
          );
        })}
      </div>

      <div className="mt-4 sm:mt-6">
        <p className="text-sm text-gray-600 mb-2">Have a specific goal in mind? Add it here:</p>
        <div className="flex gap-2">
          <input
            type="text"
            value={customGoal}
            onChange={(e) => setCustomGoal(e.target.value)}
            placeholder="Enter your business goal"
            className="flex-1 px-3 sm:px-4 py-2.5 sm:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
            disabled={selectedGoals.length >= MAX_SELECTIONS}
          />
          <button
            onClick={handleCustomGoal}
            disabled={!customGoal.trim() || selectedGoals.length >= MAX_SELECTIONS}
            className="p-2.5 text-blue-600 hover:text-blue-700 disabled:opacity-50 min-h-[44px] min-w-[44px] flex items-center justify-center"
          >
            <PlusCircle className="w-6 h-6" />
          </button>
        </div>
      </div>
    </div>
  );
}

// Default export
export default GoalsStep;